import React from "react";

import { flattenToDotNotation } from "utils";

import { Flex, Spin, Typography } from "components";

import { ChangedField, DiffResult } from "pages/admin/practiceManagementService/usePracticeManagementDiff";

export const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v)) return v;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

const expandField = (f: ChangedField): ChangedField[] => {
  const before = isPlainObject(f.before) ? f.before : {};
  const after = isPlainObject(f.after) ? f.after : {};
  if (!Object.keys(before).length && !Object.keys(after).length) return [f];
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...allKeys]
    .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
    .flatMap((key) => expandField({ field: `${f.field}.${key}`, before: before[key], after: after[key] }));
};

type ChangesSummaryProps = {
  isLoading: boolean;
  endpointSummaries: Array<{ label: string; diff: DiffResult }>;
  fromVersion: string | undefined;
  toVersion: string | undefined;
};

export const ChangesSummary = ({ isLoading, endpointSummaries, fromVersion, toVersion }: ChangesSummaryProps) => (
  <Flex vertical gap={16} className="margin-50">
    <Typography variant="paragraph-m" bold>
      Comparing '{fromVersion ?? "draft"}' against version '{toVersion}':
    </Typography>
    {isLoading ? (
      <Flex justify="center" className="margin-100">
        <Spin />
      </Flex>
    ) : (
      <div className="pms-diff-list">
        <Flex vertical gap={12}>
          {endpointSummaries.map(({ label, diff }) => (
            <Flex key={label} vertical gap={8}>
              <Flex justify="space-between" align="center">
                <Typography variant="paragraph-m" bold={diff.hasChanges} color={diff.hasChanges ? undefined : "grey-600"}>
                  {label}
                </Typography>
                {diff.hasChanges ? (
                  <Flex gap={12}>
                    {diff.added > 0 && (
                      <Typography variant="paragraph-m" bold color="green-400">
                        +{diff.added} added
                      </Typography>
                    )}
                    {diff.modified > 0 && (
                      <Typography variant="paragraph-m" bold className="text-warning">
                        ~{diff.modified} modified
                      </Typography>
                    )}
                    {diff.deleted > 0 && (
                      <Typography variant="paragraph-m" bold color="red-500">
                        -{diff.deleted} deleted
                      </Typography>
                    )}
                  </Flex>
                ) : (
                  <Typography variant="paragraph-m" color="grey-600">
                    no changes
                  </Typography>
                )}
              </Flex>
              {diff.addedItems.map((item, i) => (
                <div key={i} className="pms-diff-record">
                  <Typography variant="paragraph-s" bold color="green-400">
                    Added
                  </Typography>
                  {Object.entries(flattenToDotNotation(item)).map(([key, value]) => (
                    <div key={key} className="pms-diff-field">
                      <Typography variant="paragraph-m">{key}</Typography>
                      <Typography variant="paragraph-m" color="green-400">
                        {formatValue(value)}
                      </Typography>
                    </div>
                  ))}
                </div>
              ))}
              {diff.removedItems.map((item, i) => (
                <div key={i} className="pms-diff-record">
                  <Typography variant="paragraph-s" bold color="red-500">
                    Removed
                  </Typography>
                  {Object.entries(flattenToDotNotation(item)).map(([key, value]) => (
                    <div key={key} className="pms-diff-field">
                      <Typography variant="paragraph-m">{key}</Typography>
                      <Typography variant="paragraph-m" color="red-500">
                        {formatValue(value)}
                      </Typography>
                    </div>
                  ))}
                </div>
              ))}
              {diff.changedItems.map((item, i) => (
                <div key={i} className="pms-diff-record">
                  <Typography variant="paragraph-s" color="grey-600">
                    {Object.entries(item.identity)
                      .map(([k, v]) => `${k}: ${formatValue(v)}`)
                      .join(" | ")}
                  </Typography>
                  {item.changedFields.flatMap(expandField).map((f) => (
                    <div key={f.field} className="pms-diff-field">
                      <Typography variant="paragraph-m">{f.field}</Typography>
                      <Typography variant="paragraph-m" color="red-500">
                        {formatValue(f.before)}
                      </Typography>
                      <Typography variant="paragraph-m" color="grey-600">
                        →
                      </Typography>
                      <Typography variant="paragraph-m" color="green-400">
                        {formatValue(f.after)}
                      </Typography>
                    </div>
                  ))}
                </div>
              ))}
            </Flex>
          ))}
        </Flex>
      </div>
    )}
  </Flex>
);
